import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import classNames from "classnames";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronDown } from "../icons";
import styles from "./style.module.scss";

/** Reporting window as YYYY-MM-DD strings (what the analytics endpoints expect). */
export interface DateRange {
  from: string;
  to: string;
}

const ISO = "YYYY-MM-DD";
const iso = (d: Date) => moment(d).format(ISO);
const disp = (s: string) => moment(s, ISO).format("DD/MM/YYYY");

/** Default dashboard window — last 30 days up to today. */
export const defaultDateRange = (): DateRange => ({
  from: moment().subtract(29, "days").format(ISO),
  to: moment().format(ISO),
});

type Preset = { label: string; range: () => [Date, Date] };
const PRESETS: Preset[] = [
  { label: "Today", range: () => [moment().toDate(), moment().toDate()] },
  { label: "Yesterday", range: () => [moment().subtract(1, "day").toDate(), moment().subtract(1, "day").toDate()] },
  { label: "This week", range: () => [moment().startOf("isoWeek").toDate(), moment().toDate()] },
  { label: "Last week", range: () => [moment().subtract(1, "week").startOf("isoWeek").toDate(), moment().subtract(1, "week").endOf("isoWeek").toDate()] },
  { label: "Last 30 days", range: () => [moment().subtract(29, "days").toDate(), moment().toDate()] },
  { label: "This month", range: () => [moment().startOf("month").toDate(), moment().toDate()] },
  { label: "Last month", range: () => [moment().subtract(1, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()] },
  { label: "This year", range: () => [moment().startOf("year").toDate(), moment().toDate()] },
  { label: "Last year", range: () => [moment().subtract(1, "year").startOf("year").toDate(), moment().subtract(1, "year").endOf("year").toDate()] },
];

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

/** Date-range picker with preset shortcuts + a two-month range calendar. */
export default function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<Date | null>(moment(value.from, ISO).toDate());
  const [end, setEnd] = useState<Date | null>(moment(value.to, ISO).toDate());
  // two months side by side only where there's room; one on tablet/phone
  const [months, setMonths] = useState(2);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => setMonths(window.innerWidth > 1024 ? 2 : 1);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // keep the calendar in sync when the range changes elsewhere
  useEffect(() => {
    setStart(moment(value.from, ISO).toDate());
    setEnd(moment(value.to, ISO).toDate());
  }, [value.from, value.to]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const apply = (s: Date, e: Date) => {
    onChange({ from: iso(s), to: iso(e) });
    setOpen(false);
  };

  return (
    <div className={styles.wrap} ref={ref}>
      <button
        type="button"
        className={classNames(styles.trigger, open && styles.triggerOpen)}
        onClick={() => setOpen((v) => !v)}
      >
        {/* concrete colours (not CSS var()) so the icon survives the html2canvas
            PDF capture — var() in an inline SVG breaks the whole button subtree
            and the date text would come out blank */}
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9A469B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="17" rx="2" />
          <path d="M3 9h18M8 2v4M16 2v4" />
        </svg>
        <span className={styles.label}>
          {disp(value.from)} - {disp(value.to)}
        </span>
        <ChevronDown color="#868E96" />
      </button>

      {open && (
        <div className={styles.pop}>
          <div className={styles.presets}>
            {PRESETS.map((p) => {
              const [ps, pe] = p.range();
              // highlight the shortcut that matches the range currently applied
              const active = iso(ps) === value.from && iso(pe) === value.to;
              return (
                <button
                  type="button"
                  key={p.label}
                  className={classNames(
                    styles.preset,
                    active && styles.presetActive,
                  )}
                  onClick={() => {
                    const [s, e] = p.range();
                    setStart(s);
                    setEnd(e);
                    apply(s, e);
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className={styles.calWrap}>
            <DatePicker
              selected={start}
              onChange={(dates: any) => {
                const [s, e] = dates as [Date | null, Date | null];
                setStart(s);
                setEnd(e);
                // apply as soon as both ends of the range are picked
                if (s && e) apply(s, e);
              }}
              startDate={start ?? undefined}
              endDate={end ?? undefined}
              maxDate={moment().toDate()}
              selectsRange
              monthsShown={months}
              inline
            />
          </div>
        </div>
      )}
    </div>
  );
}
